<template>
    <div class="p-1">
        <div v-if="cargando" class="text-center py-2 text-muted small">
            <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
            Cargando detalle...
        </div>

        <!-- Cabecera -->
        <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
            <span class="badge" :class="claseEstado(negocio.estado)">{{ negocio.estado_nombre || negocio.estado }}</span>
            <span class="badge bg-light text-dark border">{{ negocio.tipo_nombre || negocio.tipo }}</span>
            <span v-if="negocio.modalidad" class="badge bg-light text-dark border text-uppercase">{{ negocio.modalidad }}</span>
            <span v-if="negocio.transaction_id" class="text-xs text-muted">
                Módulo anterior: #{{ negocio.transaction_id }}
            </span>
            <span class="text-xs text-muted ms-auto">
                <i class="fas fa-lock me-1"></i>Solo lectura
            </span>
        </div>

        <div v-if="negocio.cancelacion" class="alert alert-danger text-white text-sm py-2 mb-3">
            <i class="fas fa-ban me-1"></i>
            <strong>Cancelada</strong>
            <span v-if="negocio.cancelacion.fecha"> el {{ formatFecha(negocio.cancelacion.fecha) }}</span>
            <span v-if="negocio.cancelacion.por"> por el usuario #{{ negocio.cancelacion.por }}</span>
            <span v-if="negocio.cancelacion.motivo">: {{ negocio.cancelacion.motivo }}</span>
        </div>

        <!-- Pestañas -->
        <ul class="detalle-tabs mb-3">
            <li v-for="tab in tabs" :key="tab.key">
                <button
                    type="button"
                    :class="{ active: tabActiva === tab.key }"
                    @click="tabActiva = tab.key"
                >
                    <i :class="tab.icon" class="me-1"></i>
                    {{ tab.label }}
                    <span v-if="tab.count != null" class="tab-count">{{ tab.count }}</span>
                </button>
            </li>
        </ul>

        <!-- Resumen -->
        <div v-if="tabActiva === 'resumen'">
            <dl class="detalle-grid mb-3">
                <dt>Inmobiliaria</dt>
                <dd>
                    <template v-if="negocio.inmobiliaria">
                        <span class="fw-semibold">{{ negocio.inmobiliaria.nombre }}</span>
                        <span class="text-muted text-xs ms-1">{{ negocio.inmobiliaria.email }}</span>
                    </template>
                    <span v-else>—</span>
                </dd>

                <dt>Inmueble</dt>
                <dd>
                    <template v-if="negocio.inmueble">
                        <span class="fw-semibold">{{ negocio.inmueble.titulo || `#${negocio.inmueble.id}` }}</span>
                        <span v-if="negocio.inmueble.codigo_ficha" class="badge bg-light text-dark border ms-1">
                            {{ negocio.inmueble.codigo_ficha }}
                        </span>
                        <div class="text-muted text-xs">{{ negocio.inmueble.direccion }}</div>
                    </template>
                    <span v-else>—</span>
                </dd>

                <dt>Creado</dt>
                <dd>{{ formatFecha(negocio.created_at) }}</dd>

                <template v-if="negocio.confirmado_at">
                    <dt>Confirmado</dt>
                    <dd>{{ formatFecha(negocio.confirmado_at) }}</dd>
                </template>

                <dt>Actualizado</dt>
                <dd>{{ formatFecha(negocio.updated_at) }}</dd>
            </dl>

            <div v-if="pasosIncompletos.length" class="mb-3">
                <h6 class="bloque-titulo">Pasos del asistente</h6>
                <div class="d-flex flex-wrap gap-2">
                    <span
                        v-for="paso in negocio.pasos"
                        :key="paso.paso"
                        class="badge"
                        :class="paso.completo ? 'bg-success' : 'bg-warning text-dark'"
                        :title="paso.faltantes?.length ? `Faltan: ${paso.faltantes.join(', ')}` : ''"
                    >
                        {{ paso.paso }}. {{ paso.nombre }}
                        <i class="fas ms-1" :class="paso.completo ? 'fa-check' : 'fa-exclamation'"></i>
                    </span>
                </div>
            </div>

            <div v-if="filasResumen.length" class="mb-3">
                <h6 class="bloque-titulo">Negocio</h6>
                <dl class="valores-grid">
                    <template v-for="fila in filasResumen" :key="fila.clave">
                        <dt>{{ fila.etiqueta }}</dt>
                        <dd>
                            <span
                                v-if="fila.campo === 'vigencia' && fila.raw"
                                class="badge"
                                :class="claseVigencia(fila.raw)"
                            >{{ fila.valor }}</span>
                            <template v-else>{{ fila.valor }}</template>
                        </dd>
                    </template>
                </dl>
            </div>

            <div v-if="filasCalculos.length">
                <h6 class="bloque-titulo">Cálculos</h6>
                <dl class="valores-grid">
                    <template v-for="fila in filasCalculos" :key="fila.clave">
                        <dt>{{ fila.etiqueta }}</dt>
                        <dd>{{ fila.valor }}</dd>
                    </template>
                </dl>
            </div>
        </div>

        <!-- Partes -->
        <div v-else-if="tabActiva === 'partes'">
            <div class="row g-3">
                <div v-for="(participante, lado) in negocio.participantes || {}" :key="lado" class="col-md-6">
                    <div class="parte-card h-100">
                        <p class="parte-card__rotulo">{{ participante?.rotulo || lado }}</p>
                        <template v-if="participante">
                            <p class="parte-card__nombre">
                                {{ participante.nombre || '—' }}
                                <span class="badge ms-1" :class="claseOrigen(participante.origen)">{{ participante.origen }}</span>
                            </p>
                            <p v-if="participante.empresa" class="parte-card__dato">
                                <i class="fas fa-building me-1"></i>{{ participante.empresa }}
                            </p>
                            <p v-if="participante.ciudad" class="parte-card__dato">
                                <i class="fas fa-map-marker-alt me-1"></i>{{ participante.ciudad }}
                            </p>
                            <template v-if="participante.externo">
                                <p v-if="participante.externo.telefono" class="parte-card__dato">
                                    <i class="fas fa-phone me-1"></i>{{ participante.externo.telefono }}
                                </p>
                                <p v-if="participante.externo.email" class="parte-card__dato">
                                    <i class="fas fa-envelope me-1"></i>{{ participante.externo.email }}
                                </p>
                            </template>
                        </template>
                        <p v-else class="parte-card__dato">Sin asignar</p>
                    </div>
                </div>

                <div
                    v-for="contacto in contactos"
                    :key="contacto.rotulo"
                    class="col-md-6"
                >
                    <div class="parte-card h-100">
                        <p class="parte-card__rotulo">{{ contacto.rotulo }}</p>
                        <template v-if="contacto.datos">
                            <p class="parte-card__nombre">{{ contacto.datos.nombre || '—' }}</p>
                            <p v-if="contacto.datos.documento" class="parte-card__dato">
                                <i class="fas fa-id-card me-1"></i>{{ contacto.datos.documento }}
                            </p>
                            <p v-if="contacto.datos.telefono" class="parte-card__dato">
                                <i class="fas fa-phone me-1"></i>{{ contacto.datos.telefono }}
                            </p>
                            <p v-if="contacto.datos.email" class="parte-card__dato">
                                <i class="fas fa-envelope me-1"></i>{{ contacto.datos.email }}
                            </p>
                        </template>
                        <p v-else class="parte-card__dato">Sin asignar</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Datos financieros -->
        <div v-else-if="tabActiva === 'financieros'">
            <p v-if="!negocio.negocio" class="text-sm text-muted">Sin datos del negocio.</p>
            <template v-else>
                <p v-if="negocio.negocio.iva_porcentaje != null" class="text-sm mb-3">
                    Tasa de IVA {{ negocio.estado === 'borrador' ? 'vigente' : 'congelada al confirmar' }}:
                    <strong>{{ formatCampo('iva_porcentaje', negocio.negocio.iva_porcentaje) }}</strong>
                </p>

                <div v-if="filasDatos.length" class="mb-3">
                    <h6 class="bloque-titulo">Datos del formulario</h6>
                    <dl class="valores-grid">
                        <template v-for="fila in filasDatos" :key="fila.clave">
                            <dt>{{ fila.etiqueta }}</dt>
                            <dd class="text-break">{{ fila.valor }}</dd>
                        </template>
                    </dl>
                </div>

                <div v-if="filasCalculos.length" class="mb-3">
                    <h6 class="bloque-titulo">Cálculos</h6>
                    <dl class="valores-grid">
                        <template v-for="fila in filasCalculos" :key="fila.clave">
                            <dt>{{ fila.etiqueta }}</dt>
                            <dd>{{ fila.valor }}</dd>
                        </template>
                    </dl>
                </div>

                <details v-if="tieneDatos(negocio.negocio.foto_confirmacion)" class="mb-2">
                    <summary class="bloque-titulo">Foto de las partes al confirmar</summary>
                    <pre class="bloque-json">{{ json(negocio.negocio.foto_confirmacion) }}</pre>
                </details>

                <details v-if="tieneDatos(negocio.negocio.inmueble_antes)">
                    <summary class="bloque-titulo">Estado del inmueble antes de confirmar</summary>
                    <pre class="bloque-json">{{ json(negocio.negocio.inmueble_antes) }}</pre>
                </details>
            </template>
        </div>

        <!-- Documentos -->
        <div v-else-if="tabActiva === 'documentos'">
            <p v-if="!documentos.length" class="text-sm text-muted">No hay documentos.</p>
            <div v-else class="table-responsive">
                <table class="table mb-0">
                    <thead>
                        <tr>
                            <th>Documento</th>
                            <th>Tipo</th>
                            <th>Tamaño</th>
                            <th>Subido</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="doc in documentos" :key="doc.id">
                            <td class="text-break">
                                <i class="fas fa-file-alt text-secondary me-1"></i>
                                {{ doc.nombre }}
                            </td>
                            <td>{{ doc.tipo_nombre || doc.tipo }}</td>
                            <td class="text-nowrap">{{ formatTamano(doc.tamano_bytes) }}</td>
                            <td class="text-nowrap">
                                {{ formatFecha(doc.fecha) }}
                                <span v-if="doc.subido_por" class="d-block text-xxs text-muted">{{ doc.subido_por }}</span>
                            </td>
                            <td class="text-end">
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-primary mb-0 text-nowrap"
                                    :disabled="!!descargando"
                                    @click="$emit('descargar', doc)"
                                >
                                    <span
                                        v-if="descargando === doc.id"
                                        class="spinner-border spinner-border-sm me-1"
                                        role="status"
                                    ></span>
                                    <i v-else class="fas fa-download me-1"></i>
                                    Descargar
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <p class="text-xxs text-muted mt-2 mb-0">
                <i class="fas fa-info-circle me-1"></i>
                El enlace de descarga vence a los 5 minutos y la descarga queda en el historial del negocio con tu usuario.
            </p>
        </div>

        <!-- Historial -->
        <div v-else-if="tabActiva === 'historial'">
            <p v-if="!historial.length" class="text-sm text-muted">Sin historial.</p>
            <ul v-else class="historial-lista">
                <li v-for="(evento, i) in historial" :key="i">
                    <div class="d-flex flex-wrap justify-content-between gap-2">
                        <span class="fw-semibold text-sm text-dark">{{ accionHistorial(evento.accion) }}</span>
                        <span class="text-xs text-muted">
                            {{ formatFecha(evento.fecha) }}<template v-if="evento.usuario"> · {{ evento.usuario }}</template>
                        </span>
                    </div>
                    <ul v-if="cambiosDe(evento.detalle).length" class="cambios-lista">
                        <li v-for="cambio in cambiosDe(evento.detalle)" :key="cambio.clave">
                            <span class="text-muted">{{ cambio.etiqueta }}:</span>
                            <template v-if="cambio.esCambio">
                                <span class="valor-antes">{{ cambio.antes }}</span>
                                <i class="fas fa-long-arrow-alt-right mx-1 text-muted"></i>
                                <span class="valor-despues">{{ cambio.despues }}</span>
                            </template>
                            <span v-else class="ms-1">{{ cambio.valor }}</span>
                        </li>
                    </ul>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import {
    formatFecha,
    formatCampo,
    formatTamano,
    rotulo,
    accionHistorial,
    claseEstado,
    claseVigencia,
} from '../formatoNegocios.js';

// En el resumen ya se muestran con su propio formato
const OCULTOS_RESUMEN = ['pagador_comision'];

// Recorre un objeto anidado y devuelve una fila por valor final
const aplanar = (objeto, ruta = []) => {
    const filas = [];
    Object.entries(objeto || {}).forEach(([campo, valor]) => {
        const camino = [...ruta, campo];
        if (valor && typeof valor === 'object' && !Array.isArray(valor)) {
            filas.push(...aplanar(valor, camino));
            return;
        }
        filas.push({
            clave: camino.join('.'),
            campo,
            raw: valor,
            etiqueta: camino.map(rotulo).join(' › '),
            valor: Array.isArray(valor) ? valor.join(', ') || '—' : formatCampo(campo, valor),
        });
    });
    return filas;
};

export default {
    name: 'DetalleNegocio',
    props: {
        negocio: { type: Object, required: true },
        cargando: { type: Boolean, default: false },
        descargando: { type: [Number, String], default: null }
    },
    emits: ['descargar'],
    data() {
        return { tabActiva: 'resumen' };
    },
    computed: {
        documentos() {
            return this.negocio.documentos || [];
        },
        historial() {
            return this.negocio.historial || [];
        },
        tabs() {
            return [
                { key: 'resumen', label: 'Resumen', icon: 'fas fa-file-invoice-dollar' },
                { key: 'partes', label: 'Partes', icon: 'fas fa-users' },
                { key: 'financieros', label: 'Datos financieros', icon: 'fas fa-calculator' },
                { key: 'documentos', label: 'Documentos', icon: 'fas fa-folder-open', count: this.documentos.length },
                { key: 'historial', label: 'Historial', icon: 'fas fa-history', count: this.historial.length },
            ];
        },
        pasosIncompletos() {
            return (this.negocio.pasos || []).filter((p) => !p.completo);
        },
        filasResumen() {
            return aplanar(this.negocio.resumen_negocio).filter((f) => !OCULTOS_RESUMEN.includes(f.campo));
        },
        filasCalculos() {
            return aplanar(this.negocio.negocio?.calculos);
        },
        filasDatos() {
            return aplanar(this.negocio.negocio?.datos);
        },
        contactos() {
            return [
                { rotulo: 'Propietario', datos: this.negocio.propietario },
                { rotulo: this.negocio.cliente_cierre_rotulo || 'Cliente', datos: this.negocio.cliente_cierre },
            ];
        }
    },
    methods: {
        formatFecha,
        formatCampo,
        formatTamano,
        accionHistorial,
        claseEstado,
        claseVigencia,
        claseOrigen(origen) {
            switch (origen) {
                case 'interno':
                    return 'bg-info';
                case 'mls':
                    return 'bg-primary';
                default:
                    return 'bg-secondary';
            }
        },
        tieneDatos(valor) {
            if (valor == null) return false;
            if (Array.isArray(valor)) return valor.length > 0;
            if (typeof valor === 'object') return Object.keys(valor).length > 0;
            return valor !== '';
        },
        json(valor) {
            return typeof valor === 'string' ? valor : JSON.stringify(valor, null, 2);
        },
        // { campo: { antes, despues } } en cualquier nivel se muestra como cambio
        cambiosDe(detalle, ruta = []) {
            if (!detalle || typeof detalle !== 'object') return [];
            const filas = [];
            Object.entries(detalle).forEach(([campo, valor]) => {
                const camino = [...ruta, campo];
                const clave = camino.join('.');
                const etiqueta = camino.map(rotulo).join(' › ');
                if (valor && typeof valor === 'object' && !Array.isArray(valor)) {
                    if ('antes' in valor || 'despues' in valor) {
                        filas.push({
                            clave,
                            etiqueta,
                            esCambio: true,
                            antes: this.valorHistorial(campo, valor.antes),
                            despues: this.valorHistorial(campo, valor.despues),
                        });
                    } else {
                        filas.push(...this.cambiosDe(valor, camino));
                    }
                    return;
                }
                filas.push({ clave, etiqueta, esCambio: false, valor: this.valorHistorial(campo, valor) });
            });
            return filas;
        },
        valorHistorial(campo, valor) {
            if (valor && typeof valor === 'object') return JSON.stringify(valor);
            return formatCampo(campo, valor);
        }
    }
};
</script>

<style lang="scss" scoped>
.detalle-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    list-style: none;
    padding: 0 0 0;
    margin: 0;
    border-bottom: 1px solid #E2E8F0;

    button {
        border: none;
        background: none;
        padding: 8px 12px;
        font-size: 0.8rem;
        font-weight: 600;
        color: #64748B;
        border-bottom: 2px solid transparent;
        margin-bottom: -1px;

        &:hover {
            color: #1E293B;
        }

        &.active {
            color: #2563EB;
            border-bottom-color: #2563EB;
        }
    }
}

.tab-count {
    display: inline-block;
    min-width: 18px;
    padding: 0 5px;
    margin-left: 4px;
    border-radius: 999px;
    background: #F1F5F9;
    color: #475569;
    font-size: 0.68rem;
    text-align: center;
}

.detalle-grid {
    display: grid;
    grid-template-columns: 120px 1fr;
    gap: 0.4rem 1rem;
    font-size: 0.85rem;

    dt {
        color: #8392ab;
        font-weight: 600;
    }

    dd {
        margin: 0;
        color: #344767;
        min-width: 0;
    }
}

.valores-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr) auto);
    gap: 0.35rem 1rem;
    font-size: 0.8rem;
    background: #F8FAFC;
    border: 1px solid #F1F5F9;
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    margin: 0;

    dt {
        color: #64748B;
        font-weight: 600;
    }

    dd {
        margin: 0;
        color: #1E293B;
        text-align: right;
        font-variant-numeric: tabular-nums;
    }
}

.bloque-titulo {
    font-size: 0.8rem;
    font-weight: 700;
    color: #344767;
    margin-bottom: 0.4rem;
}

summary.bloque-titulo {
    cursor: pointer;
}

.bloque-json {
    background: #0f172a;
    color: #e2e8f0;
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    font-size: 0.75rem;
    max-height: 320px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-word;
}

.parte-card {
    border: 1px solid #E2E8F0;
    border-radius: 0.75rem;
    padding: 0.85rem 1rem;

    p {
        margin: 0;
    }

    &__rotulo {
        font-size: 0.68rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #94A3B8;
        margin-bottom: 4px !important;
    }

    &__nombre {
        font-size: 0.9rem;
        font-weight: 600;
        color: #1E293B;
        margin-bottom: 4px !important;
    }

    &__dato {
        font-size: 0.78rem;
        color: #64748B;

        i {
            width: 14px;
            color: #94A3B8;
        }
    }
}

.historial-lista {
    list-style: none;
    padding: 0;
    margin: 0;

    > li {
        padding: 0.6rem 0;
        border-bottom: 1px solid #F1F5F9;

        &:last-child {
            border-bottom: none;
        }
    }
}

.cambios-lista {
    list-style: none;
    padding: 0.4rem 0 0 0.75rem;
    margin: 0;
    font-size: 0.75rem;

    li {
        padding: 1px 0;
    }
}

.valor-antes {
    margin-left: 4px;
    color: #94A3B8;
    text-decoration: line-through;
}

.valor-despues {
    color: #1E293B;
    font-weight: 600;
}

@media (max-width: 768px) {
    .valores-grid {
        grid-template-columns: minmax(0, 1fr) auto;
    }
}

@media (max-width: 576px) {
    .detalle-grid {
        grid-template-columns: 1fr;

        dd {
            margin-bottom: 0.4rem;
        }
    }
}
</style>
